import styled from "@emotion/styled";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import RenderChildren from "./RenderChildren";
import { useShoppingCart } from "./context/ShoppingCartContext";
import { SingleProductComponent as SingleProductComponent_ } from "./interfaces/base";
import usePageLanguage from "./hooks/usePageLanguage";
import { pf, resolveProductImageUrl } from "./utilities/helpers";
import {
  getCatalogProductDisplayFields,
  getProductPageBaseHref,
  resolveCatalogProductPricing,
} from "./utilities/catalogProductAdapter";
import {
  buildCatalogProductDetailHref,
  getVariantPriceRange,
  hasVariants,
} from "./utilities/productCatalog";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import type { Product } from "./interfaces/catalogProduct";
import type { WebpageForStorefront } from "./interfaces/webpageForStorefront";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

const languageTranslations = {
  et: {
    view_product: "VAATA TOODET",
  },
  en: {
    view_product: "VIEW PRODUCT",
  },
};

export default function SingleProductComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const lang = usePageLanguage();

  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as SingleProductComponent_) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "SingleProductComponent",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as SingleProductComponent_);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );

  const { listableCatalogProducts, pricingNow } = useShoppingCart();

  useEffect(() => {
    const productUuid =
      (component.attributes as { productUuid?: string }).productUuid ??
      undefined;
    if (productUuid) {
      const found = listableCatalogProducts.find(
        (p) => p.productUuid === productUuid
      );
      if (found) {
        setSelectedProduct(found);
        return;
      }
    }
    const legacyId = component.attributes.productId;
    if (typeof legacyId === "number" && legacyId > 0) {
      setSelectedProduct(listableCatalogProducts[0] ?? null);
    }
  }, [component, listableCatalogProducts]);

  const productItemRef = useRef<HTMLDivElement>(null);
  const pages = useAppSelector((state) => state.builder.pages.list);

  const productPage = (pages as WebpageForStorefront[]).find(
    (page) => page.pageType === "product" && page.languageCode === lang
  );
  const languageProductHref = getProductPageBaseHref(productPage, languages);

  const pricing = useMemo(() => {
    if (!selectedProduct) {
      return null;
    }
    return resolveCatalogProductPricing(selectedProduct, pricingNow);
  }, [selectedProduct, pricingNow]);

  const display = useMemo(() => {
    if (!selectedProduct) {
      return null;
    }
    return getCatalogProductDisplayFields(selectedProduct, lang);
  }, [selectedProduct, lang]);

  const productDetailHref = useMemo(() => {
    if (!selectedProduct) {
      return languageProductHref;
    }
    return buildCatalogProductDetailHref(
      languageProductHref,
      selectedProduct,
      lang
    );
  }, [languageProductHref, selectedProduct, lang]);

  const priceRange = selectedProduct
    ? getVariantPriceRange(selectedProduct)
    : null;
  const variantMode = selectedProduct ? hasVariants(selectedProduct) : false;

  return (
    <Div
      id={id.toString()}
      data-component-type="singleProduct"
      className={
        (attributes.classes ?? "") +
        " builder-component builder-component__singleProduct" +
        (matchesEditorTarget({ id, uuid }, editorId)
          ? " builder-component--active"
          : "")
      }
      data-style={attributes && attributes.styles}
    >
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />
      {selectedProduct && display && pricing && (
        <div className="single-product" ref={productItemRef}>
          <a href={productDetailHref}>
            {display.firstFileUrl && (
              <img
                src={resolveProductImageUrl(display.firstFileUrl)}
                alt={display.displayName}
              />
            )}
            <h3>{display.displayName}</h3>
            <div className="productPrice">
              {!variantMode && (
                <span>{pf(pricing.price_total)} &euro;</span>
              )}
              {variantMode && priceRange && (
                <span>
                  {priceRange.minPrice !== priceRange.maxPrice
                    ? `${pf(priceRange.minPrice)} - ${pf(priceRange.maxPrice)}`
                    : pf(priceRange.minPrice)}{" "}
                  &euro;
                </span>
              )}
            </div>
            <span className="btn btn-primary">
              {languageTranslations[lang]?.view_product}
            </span>
          </a>
        </div>
      )}
    </Div>
  );
}
